// @flow strict

import React from 'react';
import { matchPath } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { TabNav } from 'components/_ReactUI_V1';

import { NAVIGATION_ROUTES } from 'utils/constants';
import { tryParseNumberOrNull } from 'utils/helpers';
import { SolvExtractNavigation } from 'utils/SolvExtractNavigation';
import { canAccessPlantImportFeatures } from 'utils/authentication';

import { selectUser } from 'services/Authentication/selectors';
import { selectCircuit, selectAllCircuitTrends } from 'services/Trends/selectors';

import type { ImmutableList, IntlType, RouteType } from 'types';
import type { ImmutableUser } from 'services/Authentication/types';
import type { ImmutableCircuit } from 'services/Circuit/types';
import type { ImmutableCircuitTrend } from 'services/Trends/types';

type Props = {
    currentLocation: string,
    route: RouteType,
    intl: IntlType,
    user: ImmutableUser,
    circuit: ?ImmutableCircuit,
    circuitTrends: ImmutableList<ImmutableCircuitTrend>,
};

class SolvExtractPageNav extends React.PureComponent<Props> {
    getRouteParams = () => {
        const match = matchPath(this.props.currentLocation, {
            path: this.props.route.path,
            exact: true,
            strict: false,
        });
        if (!match) {
            return { plantId: null, circuitId: null };
        }
        return {
            plantId: tryParseNumberOrNull(match.params.plantId),
            circuitId: tryParseNumberOrNull(match.params.circuitId),
        };
    };

    getPlantIdFromCircuit = (circuitId: ?number) => {
        const { circuit, circuitTrends } = this.props;
        if (circuit) {
            return circuit.get('plantId', null);
        }
        if (circuitTrends && circuitId) {
            const trend = circuitTrends.find(
                (t: ImmutableCircuitTrend) => t.get('circuitId') === circuitId
            );
            return trend ? trend.get('plantId', null) : null;
        }
        return null;
    };

    isPlantPage = () => this.props.route.path.startsWith(NAVIGATION_ROUTES.PLANT);

    /**
     * URL patterns handled:
     *
     * Plant pages (no circuitId in URL):
     *   /plant/123          → dashboard
     *   /plant/123/archive  → archive
     *   /plant/123/trends   → legacy trends
     *   /plant/123/reports  → reports
     *
     * Circuit pages (circuitId in URL):
     *   /plant/123/circuit/456  → v2 dashboard (has plantId)
     *   /circuit/456/trends     → legacy trends (no plantId, must resolve from circuit data)
     *   /circuit/456/archive    → archive (no plantId, must resolve from circuit data)
     */
    getDashboardUrl = (plantId: ?number, circuitId: ?number) => {
        if (!circuitId) {
            if(plantId) {
                return SolvExtractNavigation.getPlantDashboardUrl(plantId);
            }
            // Both IDs are missing !!!
            console.warn('Missing circuit id and plant id for dashboard url navigation.');
            return null;
        }

        const resolvedPlantId = plantId || this.getPlantIdFromCircuit(circuitId);
        if (resolvedPlantId) {
            return SolvExtractNavigation.getCircuitDashboardUrl(resolvedPlantId, circuitId);
        }

        console.warn('Missing circuit id and plant id for dashboard url navigation.');
        return null;
    };

    getArchiveUrl = (plantId: ?number, circuitId: ?number) => {
        if (circuitId) {
            return `${NAVIGATION_ROUTES.CIRCUIT}${circuitId}${NAVIGATION_ROUTES.ARCHIVE}`;
        }
        if (plantId) {
            return `${NAVIGATION_ROUTES.PLANT}${plantId}${NAVIGATION_ROUTES.ARCHIVE}`;
        }
        console.warn('Unknown page type provided to SolvExtract page navigation.');
        return null;
    };

    render() {
        const { plantId, circuitId } = this.getRouteParams();
        const { intl, user, currentLocation } = this.props;

        const resolvedPlantId = plantId ?? this.getPlantIdFromCircuit(circuitId);

        const options = [
            {
                label: intl.formatMessage({ id: 'navigation.Trends' }),
                to: this.getDashboardUrl(plantId, circuitId),
            },
            {
                label: intl.formatMessage({ id: 'navigation.Archive' }),
                to: this.getArchiveUrl(plantId, circuitId),
            },
        ].filter((option) => option.to); // Filter out options with null URLs

        if (resolvedPlantId && canAccessPlantImportFeatures(user)) {
            const plantPrefix = `${NAVIGATION_ROUTES.PLANT}${resolvedPlantId}`;
            const importUrl = `${plantPrefix}${NAVIGATION_ROUTES.NEW_PLANT_IMPORT}`;
            const editUrl = `${plantPrefix}${NAVIGATION_ROUTES.EDIT_LATEST_IMPORT}`;

            options.push({
                label: intl.formatMessage({ id: 'navigation.PlantImport' }),
                to: importUrl,
                isActiveTab: currentLocation === importUrl || currentLocation === editUrl,
            });
        }

        if (resolvedPlantId) {
            options.push({
                label: intl.formatMessage({ id: 'navigation.Reports' }),
                to: `${NAVIGATION_ROUTES.PLANT}${resolvedPlantId}${NAVIGATION_ROUTES.PLANT_REPORTS}`,
            });
        }

        return <TabNav current={currentLocation} options={options} maxWidth="600" />;
    }
}

const mapStateToProps = createStructuredSelector({
    user: selectUser(),
    circuit: selectCircuit(),
    circuitTrends: selectAllCircuitTrends(),
});

export default connect(mapStateToProps)(injectIntl(SolvExtractPageNav));
